import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Edit, Trash2, Clock, Building2, MapPin, Tag, Hash,
  Wifi, Network, Calendar, FileText
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const RelojDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('micro', 'relojes');
  
  const [clock, setClock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formOptions, setFormOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [], ubicaciones: [], proveedores: [] });
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchClock = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/relojes/${id}`);
      setClock(response.data);
    } catch (error) {
      toast.error('Error al cargar el reloj');
      navigate('/micro/clocks');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/relojes/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchClock();
    fetchFormOptions();
  }, [id]);

  const openEditModal = () => {
    setFormData({
      empresa_id: clock.empresa_id?.toString() || '',
      site_id: clock.site_id?.toString() || '',
      brand_id: clock.brand_id?.toString() || '',
      asset_tag: clock.asset_tag || '',
      serial_number: clock.serial_number || '',
      modelo: clock.modelo || '',
      ip_address: clock.ip_address || '',
      mac_address: clock.mac_address || '',
      status_id: clock.status_id?.toString() || '',
      location_id: clock.location_id?.toString() || '',
      supplier_id: clock.supplier_id?.toString() || '',
      purchase_date: clock.purchase_date || '',
      comments: clock.comments || ''
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
      };

      await api.put(`/assets/relojes/${id}`, payload);
      toast.success('Reloj actualizado exitosamente');
      setEditModal(false);
      fetchClock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/relojes/${id}`);
      toast.success('Reloj eliminado exitosamente');
      navigate('/micro/clocks');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) return <span className="badge badge-warning">Sin Estado</span>;
    const lower = statusName.toLowerCase();
    if (lower.includes('activo')) return <span className="badge badge-success">{statusName}</span>;
    if (lower.includes('inactivo') || lower.includes('baja')) return <span className="badge badge-danger">{statusName}</span>;
    return <span className="badge badge-default">{statusName}</span>;
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Cargando...</p></div></Layout>;
  }

  if (!clock) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/micro/clocks')}>
              <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <h1 className="text-2xl font-bold">{clock.brand_name || 'Reloj'} {clock.modelo || ''}</h1>
              </div>
              <p className="text-muted-foreground font-mono">{clock.asset_tag}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={openEditModal} data-testid="edit-clock-btn">
                <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteModal(true)} data-testid="delete-clock-btn">
                <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Summary */}
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              {getStatusBadge(clock.status_name)}
              {clock.ip_address && (
                <div className="flex items-center gap-2 text-sm">
                  <Wifi className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono">{clock.ip_address}</span>
                </div>
              )}
              {clock.mac_address && (
                <div className="flex items-center gap-2 text-sm">
                  <Network className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono">{clock.mac_address}</span>
                </div>
              )}
              {clock.site_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{clock.site_name}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detail Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Hash className="h-4 w-4" strokeWidth={1.5} />Identificacion</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Asset Tag</span><span className="font-mono">{clock.asset_tag || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Serial Number</span><span className="font-mono">{clock.serial_number || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Marca</span><span>{clock.brand_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Modelo</span><span>{clock.modelo || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Network className="h-4 w-4" strokeWidth={1.5} />Red</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">IP Address</span><span className="font-mono">{clock.ip_address || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">MAC Address</span><span className="font-mono">{clock.mac_address || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" strokeWidth={1.5} />Ubicacion</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Empresa</span><span>{clock.empresa_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Site</span><span>{clock.site_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ubicacion</span><span>{clock.location_name || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" strokeWidth={1.5} />Compra y Notas</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Proveedor</span><span>{clock.supplier_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fecha de Compra</span><span>{clock.purchase_date || '-'}</span></div>
              {clock.comments && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Comentarios</p>
                  <p className="text-sm">{clock.comments}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit Modal */}
        <Dialog open={editModal} onOpenChange={setEditModal}>
          <DialogContent className="max-w-2xl" aria-describedby="edit-clock-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" strokeWidth={1.5} />Editar Reloj</DialogTitle>
            </DialogHeader>
            <p id="edit-clock-desc" className="text-sm text-muted-foreground">Modifique los datos del reloj de control de asistencia.</p>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2"><Label>Asset Tag *</Label><Input value={formData.asset_tag} onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Serial Number</Label><Input value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Marca</Label><Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.brands.map(b => <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Modelo</Label><Input value={formData.modelo} onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))} /></div>
              <div className="space-y-2"><Label>IP Address</Label><Input value={formData.ip_address} onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))} placeholder="192.168.1.100" /></div>
              <div className="space-y-2"><Label>MAC Address</Label><Input value={formData.mac_address} onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))} placeholder="00:11:22:33:44:55" /></div>
              <div className="space-y-2"><Label>Empresa *</Label><Select value={formData.empresa_id} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.empresas.map(e => <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Site *</Label><Select value={formData.site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.sites.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Ubicacion</Label><Select value={formData.location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.ubicaciones.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Estado</Label><Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.statuses.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Proveedor</Label><Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.proveedores.map(p => <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Fecha de Compra</Label><Input type="date" value={formData.purchase_date} onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))} /></div>
              <div className="space-y-2 col-span-2"><Label>Comentarios</Label><Textarea value={formData.comments} onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} rows={2} /></div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
          <DialogContent aria-describedby="del-clock-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-5 w-5" strokeWidth={1.5} />Eliminar Reloj</DialogTitle>
            </DialogHeader>
            <p id="del-clock-desc" className="text-muted-foreground">Eliminar <strong>{clock.asset_tag}</strong>?<br /><span className="text-sm">Esta accion no se puede deshacer.</span></p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RelojDetail;
